import React from 'react'
import { Drawer } from 'antd'
import { useHistory, useParams } from 'react-router-dom'

import { useToggle, stringifyDate, usePet, normalize } from '../../modules'

import Receive from '../reusable/receive'

// View-only: credit notes aren't editable once recorded (matches how invoices/payments
// in this codebase are corrected via new offsetting entries, not mutation) - this just
// shows what was recorded, including the per-line-item breakdown for return-type notes.
// Invoice item names are read off the populated source invoice rather than stored on the
// credit note item itself, since CreditNoteItem only keeps invoiceItemIndex/batchNo/
// quantity/amounts.
const CreditNoteDetail = () => {
    const { noteId } = useParams()
    const history = useHistory()
    const [visible, toggleVisible] = useToggle(true)

    const [{ loading, error, data: note }] = usePet('creditNote', { id: noteId }, { notify: true })

    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 300)
    }

    const unused = Number(note?.amount ?? 0) - Number(note?.amountApplied ?? 0)

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            width="50%">
            <Receive error={error} loading={loading} progress>
                <p className="font-extrabold text-3xl mb-0 capitalize">{ note?.type } Credit Note</p>
                <p className="text-gray-500 text-xs italic mb-3">
                    Recorded for { note?.supplier?.name } on { stringifyDate(note?.date) } by { note?.recordedBy?.name }
                    { note?.invoice?.code ? ` against invoice #${note.invoice.code}` : '' }
                </p>

                <div className="w-full flex mb-4">
                    <div className="w-1/3">
                        <p className="text-xs uppercase text-gray-400 font-semibold mb-1">Amount</p>
                        <p className="font-semibold">Ksh. { normalize(note?.amount) }</p>
                    </div>
                    <div className="w-1/3">
                        <p className="text-xs uppercase text-gray-400 font-semibold mb-1">Applied</p>
                        <p className="font-semibold">Ksh. { normalize(note?.amountApplied) }</p>
                    </div>
                    <div className="w-1/3">
                        <p className="text-xs uppercase text-gray-400 font-semibold mb-1">Unused</p>
                        <p className="font-semibold">Ksh. { normalize(unused) }</p>
                    </div>
                </div>

                <div className="w-full mb-4">
                    <p className="text-xs uppercase text-gray-400 font-semibold mb-1">Reason</p>
                    <p>{ note?.reason }</p>
                </div>

                {
                    note?.type === 'return' &&
                    <table className="table-auto w-full">
                        <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                            <tr>
                                <th className="p-2"><div className="font-semibold text-left">Item</div></th>
                                <th className="p-2"><div className="font-semibold text-left">Batch</div></th>
                                <th className="p-2"><div className="font-semibold text-center">Qty</div></th>
                                <th className="p-2"><div className="font-semibold text-center">Gross</div></th>
                                <th className="p-2"><div className="font-semibold text-center">Discount</div></th>
                                <th className="p-2"><div className="font-semibold text-center">VAT</div></th>
                            </tr>
                        </thead>
                        <tbody className="text-sm font-medium divide-y divide-gray-100">
                            {
                                (note?.items ?? []).map((item, key) =>
                                    <tr key={key}>
                                        <td className="p-2">{ note?.invoice?.items?.[item.invoiceItemIndex]?.name ?? '—' }</td>
                                        <td className="p-2">{ item.batchNo ?? '—' }</td>
                                        <td className="p-2"><div className="text-center">{ item.quantity }</div></td>
                                        <td className="p-2"><div className="text-center">Ksh. { normalize(item.grossAmount) }</div></td>
                                        <td className="p-2"><div className="text-center">Ksh. { normalize(item.discountAmount) }</div></td>
                                        <td className="p-2"><div className="text-center">Ksh. { normalize(item.vatAmount) }</div></td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                }
            </Receive>
        </Drawer>
    )
}

export default CreditNoteDetail
